import { WebSocketProvider, WebSocketConfig, WebSocketMessage } from './types';

export class NativeWSProvider extends WebSocketProvider {
  private socket: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private manualDisconnect = false;

  constructor(config: WebSocketConfig) {
    super(config);
  }

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/_ws?device_id=${this.config.deviceId}&session_id=${this.config.sessionId}`;
  }

  connect(): void {
    if (this.socket) {
      console.warn('Native WebSocket already connected');
      return;
    }

    try {
      this.socket = new WebSocket(this.getWebSocketUrl());

      this.socket.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.manualDisconnect = false;
        console.log('Native WebSocket Connected');

        // Send authorization message
        // this.sendMessage({
        //   type: 'authorize',
        //   data: {
        //     deviceIdentifier: this.config.deviceId,
        //     sessionIdentifier: this.config.sessionId,
        //   },
        // });

        if (this.config.onConnect) {
          this.config.onConnect();
        }
      };

      this.socket.onclose = (event) => {
        this.connected = false;
        console.log('Native WebSocket Disconnected', event.code, event.reason);

        if (this.config.onDisconnect) {
          this.config.onDisconnect(event.reason);
        }

        // Attempt to reconnect only if not manually disconnected
        if (!this.manualDisconnect) {
          this.attemptReconnect();
        }
      };

      this.socket.onerror = (event) => {
        console.error('Native WebSocket Error:', event);

        if (this.config.onError) {
          this.config.onError(new Error('WebSocket connection error'));
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle acknowledgment if required
          if (message.requireAck && message.messageId) {
            this.sendAck(message.messageId);
          }

          // Dispatch to the handler
          if (message.type === 'pageUpdate') {
            this.config.onPageUpdate(message);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      if (this.config.onError && error instanceof Error) {
        this.config.onError(error);
      }
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
      this.socket = null;
      this.connect();
    }, this.reconnectDelay);
  }

  disconnect(): void {
    this.manualDisconnect = true;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connected = false;
    }
  }

  isConnected(): boolean {
    return this.connected && this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  sendAck(messageId: string): void {
    this.sendMessage({
      type: 'ack',
      data: { messageId },
    });
  }

  private sendMessage(message: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Cannot send message.');
    }
  }

  setManualDisconnect(value: boolean): void {
    this.manualDisconnect = value;
  }
}
